import { toast } from 'react-toastify';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@accure/ui';
import { Loader2, Unlink } from 'lucide-react';

import { DetachTaskMutation } from '@/project-management/mutations/detach-task.mutation';
import { TaskQuery } from '@/project-management/queries/task.query';

import type { DetachTaskModalOpen } from './detach-task.provider';

export type DetachTaskModalProps = {
  open: DetachTaskModalOpen;
  onClose: () => void;
};

export function DetachTaskModal({ open, onClose }: DetachTaskModalProps) {
  return (
    <Dialog open={Boolean(open)} onOpenChange={onClose}>
      {open ? <DetachTaskModalContent id={open.id} onClose={onClose} /> : null}
    </Dialog>
  );
}

type DetachTaskModalContentProps = {
  id: string;
  onClose: () => void;
};

function DetachTaskModalContent({ id, onClose }: DetachTaskModalContentProps) {
  const detachTask = DetachTaskMutation.useMutation();
  const taskQuery = TaskQuery.useQuery({
    id,
  });

  if (taskQuery.isLoading) {
    return (
      <DialogContent className="flex min-h-[350px] items-center justify-center py-8 sm:max-w-sm">
        <Loader2 className="size-8 animate-spin text-gray-300" />
      </DialogContent>
    );
  }

  if (taskQuery.isError || !taskQuery.data) {
    throw taskQuery.error;
  }

  const { task } = taskQuery.data;

  async function handleDetachTask() {
    if (!task.workstream) {
      throw new Error('Task is not associated with a workstream');
    }

    await toast.promise(
      detachTask.mutateAsync({
        id: task.id,
        workstreamId: task.workstream.id,
      }),
      {
        pending: 'Detaching task...',
        success: 'Task detached successfully.',
        error: 'Failed to detach task. Please try again.',
      },
    );
    onClose();
  }

  return (
    <DialogContent className="gap-6 sm:max-w-sm">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2 text-red-600">
          <Unlink className="size-5" />
          Detach Task
        </DialogTitle>
      </DialogHeader>

      <div className="space-y-4">
        {task.workstream ? (
          <>
            <p className="text-gray-700">
              Are you sure you want to detach this task from the workstream? The
              task will remain in your project but will no longer be associated
              with this workstream.
            </p>

            <div className="space-y-3 rounded-lg bg-gray-50 p-4">
              <h4 className="font-medium text-gray-900">{task.title}</h4>

              <p className="text-sm text-gray-600">
                Currently in:{' '}
                <span className="font-medium">{task.workstream.name}</span>
              </p>
            </div>
          </>
        ) : (
          <p className="text-gray-700">
            This task is not associated with any workstream.
          </p>
        )}
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={onClose}>
          Cancel
        </Button>
        {task.workstream ? (
          <Button
            variant="destructive"
            onClick={handleDetachTask}
            disabled={detachTask.isLoading}
          >
            {detachTask.isLoading ? (
              <>
                <Loader2 className="size-3.5 animate-spin" />
                Detaching...
              </>
            ) : (
              <>Detach Task</>
            )}
          </Button>
        ) : null}
      </DialogFooter>
    </DialogContent>
  );
}
